/**
 * Immediate-mode canvas UI.
 *
 * Everything the player clicks is drawn here rather than in the DOM, which
 * keeps the retro look consistent and means the menus scale with the canvas
 * instead of fighting CSS.
 *
 * Keyboard navigation works off the previous frame's widget list: the layout of
 * a menu is stable frame to frame, so indices line up and arrow keys plus Enter
 * give full controller-style navigation.
 */

import { clamp } from '@shared/math.js';
import type { PointerState } from '../core/input.js';

export interface UiPalette {
  bg: string;
  panel: string;
  panelAlt: string;
  line: string;
  ink: string;
  muted: string;
  accent: string;
  accentInk: string;
  good: string;
  warn: string;
  bad: string;
}

export const PALETTE: UiPalette = {
  bg: '#0d0f14',
  panel: '#171a22',
  panelAlt: '#1f232d',
  line: '#2b303d',
  ink: '#eae8e0',
  muted: '#8f95a8',
  accent: '#f2c14e',
  accentInk: '#1a1405',
  good: '#4fbf7b',
  warn: '#f2a03d',
  bad: '#e8574a',
};

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface UiItem {
  rect: Rect;
  focusable: boolean;
  activate?: () => void;
  /** Priority hint so primary actions sort first in the tab order. */
  order: number;
  /**
   * True for widgets that do not scroll with the content (the shell's footer
   * actions), so hit-testing leaves them in place while the body pans.
   */
  pinned: boolean;
}

export interface ButtonOptions {
  variant?: 'primary' | 'ghost' | 'danger' | 'subtle';
  disabled?: boolean;
  small?: boolean;
  align?: 'left' | 'center';
  /** Set for shell footer actions: the button stays put as content scrolls. */
  pinned?: boolean;
}

const MONO = 'ui-monospace, "SFMono-Regular", "Cascadia Mono", Menlo, Consolas, monospace';

export class Ui {
  ctx: CanvasRenderingContext2D;
  pointer: PointerState;
  width = 0;
  height = 0;
  /** Global UI scale for small screens. */
  scale = 1;

  private items: UiItem[] = [];
  private previousItems: UiItem[] = [];
  private navIndex = 0;
  private hoveredIndex = -1;
  private mouseUsed = false;
  /** True while the pointer is the primary input; keyboard nav takes over otherwise. */
  private enterPressed = false;
  private keyboardNav = false;

  /**
   * Vertical scroll offset of the current screen's content, in CSS pixels.
   *
   * The shell owns this across frames. It lays the content box out at
   * `body.y - scrollY`, so widget rects end up in screen space and a screen can
   * simply lay itself out at its natural height while the player pans. This is
   * what guarantees a control can never be permanently out of reach on a short
   * window, where the layout cannot be made to fit at any legible scale.
   */
  scrollY = 0;
  scrollMax = 0;
  /** Visible content body of the current screen (set by the shell each frame). */
  body: Rect = { x: 0, y: 0, w: 0, h: 0 };
  /** Height of the scroll track, which stops above the pinned footer. */
  private scrollTrackH = 0;
  /**
   * The pinned footer band: excluded from content hit-testing and from the
   * scrollbar track. It is *not* excluded from the scroll clip — content may be
   * drawn behind it and is covered by `footerBar`.
   */
  private reservedBottom = 0;
  /** True while the scroll clip is open; closed by `endScroll`/`endFrame`. */
  private clipOpen = false;
  /** Guards the pinned-footer strip so it is painted at most once per frame. */
  private footerBarPainted = false;

  constructor(ctx: CanvasRenderingContext2D, pointer: PointerState) {
    this.ctx = ctx;
    this.pointer = pointer;
  }

  /**
   * Opens the scrollable content body: clips drawing to it and returns the
   * content box (already offset by `-scrollY`) in which the screen lays itself
   * out. The shell calls this once per frame; the clip is closed by `endScroll`.
   *
   * `reservedBottom` is the pinned footer band. It only narrows the scrollbar
   * track and the hit-testable area; the clip deliberately still covers it.
   */
  beginScroll(body: Rect, naturalHeight: number, reservedBottom = 0): Rect {
    this.body = body;
    this.reservedBottom = reservedBottom;
    this.scrollTrackH = Math.max(1, body.h - reservedBottom);
    this.scrollMax = Math.max(0, naturalHeight - body.h);
    // Mouse wheel pans the content; keyboard scrolling is handled by the shell.
    if (this.pointer.wheel && this.scrollMax > 0) this.scrollY += this.pointer.wheel * 0.9;
    this.scrollY = clamp(this.scrollY, 0, this.scrollMax);

    const ctx = this.ctx;
    ctx.save();
    ctx.beginPath();
    // The clip covers the *whole* body, including the band the pinned footer
    // actions sit on. Clipping the band away too made the footer buttons vanish:
    // a pinned button's rect starts exactly at the band's top edge, so it fell
    // entirely outside the clip and was silently never drawn.
    ctx.rect(body.x, body.y, body.w, Math.max(1, body.h));
    ctx.clip();
    this.clipOpen = true;
    return { x: body.x, y: body.y - this.scrollY, w: body.w, h: Math.max(body.h, naturalHeight) };
  }

  /** The area actually usable for content: the body minus the pinned footer band. */
  private get interactiveBody(): Rect {
    return { x: this.body.x, y: this.body.y, w: this.body.w, h: Math.max(1, this.body.h - this.reservedBottom) };
  }

  /** Height of the usable content area, in CSS pixels. */
  get interactiveBodyHeight(): number {
    return this.interactiveBody.h;
  }

  /** Scrolls by a number of pixels, clamped to the available range. */
  scrollBy(dy: number): void {
    this.scrollY = clamp(this.scrollY + dy, 0, this.scrollMax);
  }

  /**
   * Paints the opaque band the pinned footer actions sit on. Content scrolls
   * behind this band, so without it list rows would show through the gaps between
   * the buttons.
   *
   * This runs inside the scroll clip, so it covers the content column (the
   * shell's `footer` rect) rather than the whole canvas — the page backdrop still
   * shows in the margins beside the column, exactly as it does above the band.
   *
   * Idempotent: the first caller in a frame paints it and later callers are
   * no-ops. Screens draw their footer from several helpers (back button, primary
   * action, results row) and whichever runs first must not be erased by the next.
   */
  footerBar(footer: Rect): void {
    if (this.footerBarPainted) return;
    this.footerBarPainted = true;
    const { ctx } = this;
    const x = Math.round(footer.x);
    const y = Math.round(footer.y);
    const w = Math.round(footer.w);
    const h = Math.round(footer.h);
    ctx.fillStyle = PALETTE.bg;
    ctx.fillRect(x, y, w, h);
    // A hairline keeps the band reading as chrome rather than a hole in the page.
    ctx.fillStyle = PALETTE.line;
    ctx.fillRect(x, y, w, 1);
  }

  /**
   * Scrolls the minimum amount needed to bring a (screen-space) widget rect
   * inside the visible box. Widget rects move with the scroll offset, so the
   * delta computed here is exactly what the widget needs.
   */
  scrollIntoView(rect: Rect, box: Rect): void {
    if (this.scrollMax <= 0) return;
    const pad = 12 * this.scale;
    if (rect.y - pad < box.y) this.scrollBy(-(box.y - (rect.y - pad)));
    else if (rect.y + rect.h + pad > box.y + box.h) this.scrollBy(rect.y + rect.h + pad - (box.y + box.h));
  }

  beginFrame(width: number, height: number, arrowPressed: { up: boolean; down: boolean; left: boolean; right: boolean; activate: boolean }): void {
    this.width = width;
    this.height = height;
    this.scale = clamp(Math.min(width / 1000, height / 640), 0.78, 1.5);
    // Screens that lay themselves out with `shell()` (everything but the login
    // screen) overwrite these via `beginScroll`, but the defaults must still be
    // sane: leaving `body` at its zero initialiser made `contains()` reject every
    // rect, so the login fields and the Sign in button were unclickable until
    // some other screen had been visited. That only shows on a *fresh* visit,
    // where `login` is the first screen ever drawn.
    this.body = { x: 0, y: 0, w: width, h: height };
    this.reservedBottom = 0;
    this.ctx.textBaseline = 'middle';
    this.hoveredIndex = -1;
    this.footerBarPainted = false;
    this.enterPressed = arrowPressed.activate;
    this.keyboardNav = arrowPressed.up || arrowPressed.down || arrowPressed.left || arrowPressed.right;
    if (this.keyboardNav) this.mouseUsed = false;
    if (this.pointer.pressed) this.mouseUsed = true;

    const count = this.previousItems.length;
    if (count > 0) {
      // Only re-anchor the view when the focus ring actually moves. Doing it
      // every frame pinned the scroll to whatever widget held focus and undid
      // the player's own wheel/keyboard scrolling.
      let navMoved = false;
      if (arrowPressed.down || arrowPressed.right) {
        this.navIndex = (this.navIndex + 1) % count;
        navMoved = true;
      } else if (arrowPressed.up || arrowPressed.left) {
        this.navIndex = (this.navIndex - 1 + count) % count;
        navMoved = true;
      }
      this.navIndex = clamp(this.navIndex, 0, count - 1);
      if (arrowPressed.activate && !this.mouseUsed) {
        this.previousItems[this.navIndex]?.activate?.();
      }
      // Keyboard navigation must never be able to walk the focus ring off the
      // bottom of a scrolled screen.
      const focused = this.previousItems[this.navIndex];
      if (navMoved && focused) this.scrollIntoView(focused.rect, this.interactiveBody);
    }
    this.items = [];
  }

  endScroll(): void {
    if (!this.clipOpen) return;
    // The scrollbar belongs with the content it describes, so it goes on before
    // the clip comes off.
    this.drawScrollIndicator();
    this.ctx.restore();
    this.clipOpen = false;
  }

  endFrame(): void {
    this.endScroll();
    this.previousItems = this.items;
    if (this.navIndex >= this.items.length) this.navIndex = Math.max(0, this.items.length - 1);
  }

  /** A slim scrollbar on the right edge, shown only when there is content to pan. */
  private drawScrollIndicator(): void {
    if (this.scrollMax <= 0) return;
    const ctx = this.ctx;
    const body = this.body;
    const trackW = Math.max(4, Math.round(6 * this.scale));
    const x = body.x + body.w - trackW;
    const trackH = this.scrollTrackH;
    const thumbH = Math.max(28 * this.scale, trackH * (body.h / (body.h + this.scrollMax)));
    const t = this.scrollMax > 0 ? this.scrollY / this.scrollMax : 0;
    const thumbY = body.y + t * (trackH - thumbH);
    ctx.fillStyle = 'rgba(255,255,255,0.07)';
    ctx.fillRect(x, body.y, trackW, trackH);
    ctx.fillStyle = PALETTE.muted;
    ctx.fillRect(x, thumbY, trackW, thumbH);
  }

  /** Resets navigation, e.g. after switching screens. */
  resetNavigation(): void {
    this.navIndex = 0;
    this.previousItems = [];
    this.items = [];
    this.scrollY = 0;
  }

  private register(rect: Rect, focusable: boolean, activate?: () => void, order = 0, pinned = false): number {
    this.items.push({ rect, focusable, activate, order, pinned });
    return this.items.length - 1;
  }

  /**
   * Pointer-over test for a widget.
   *
   * `pinned` widgets (the shell footer) sit in screen space and are always
   * visible. Everything else is positioned relative to the shell's content box,
   * which the shell has already offset by `-scrollY` — so widget rects are in
   * screen space too, and the test is simply limited to the visible body.
   */
  private contains(rect: Rect, pinned = false): boolean {
    const p = this.pointer;
    if (p.x < rect.x || p.x > rect.x + rect.w || p.y < rect.y || p.y > rect.y + rect.h) return false;
    if (pinned) return true;
    const box = this.interactiveBody;
    if (rect.y + rect.h <= box.y + 0.5 || rect.y >= box.y + box.h - 0.5) return false;
    return true;
  }

  /**
   * Hit test for screen code that draws its own controls rather than using the
   * widgets. Accounts for the content scroll offset, so it must be used in place
   * of comparing `pointer` coordinates directly.
   */
  pointerIn(rect: Rect): boolean {
    return this.contains(rect);
  }

  // ------------------------------------------------------------ primitives
  font(size: number, weight: '400' | '600' | '700' = '400'): void {
    this.ctx.font = `${weight} ${Math.round(size * this.scale)}px ${MONO}`;
  }

  text(
    value: string,
    x: number,
    y: number,
    options: { size?: number; colour?: string; align?: CanvasTextAlign; weight?: '400' | '600' | '700'; maxWidth?: number } = {},
  ): number {
    this.font(options.size ?? 14, options.weight ?? '400');
    this.ctx.fillStyle = options.colour ?? PALETTE.ink;
    this.ctx.textAlign = options.align ?? 'left';
    this.ctx.fillText(value, x, y, options.maxWidth);
    this.ctx.textAlign = 'left';
    return this.ctx.measureText(value).width;
  }

  measure(value: string, size = 14, weight: '400' | '600' | '700' = '400'): number {
    this.font(size, weight);
    return this.ctx.measureText(value).width;
  }

  /**
   * Draws text truncated with an ellipsis when it will not fit in `maxWidth`.
   * Preferred over `maxWidth` on `text()`, which squeezes the glyphs together
   * instead of dropping them and looks broken on long labels in narrow columns.
   */
  fitText(
    value: string,
    x: number,
    y: number,
    maxWidth: number,
    options: { size?: number; colour?: string; align?: CanvasTextAlign; weight?: '400' | '600' | '700' } = {},
  ): void {
    const size = options.size ?? 14;
    this.font(size, options.weight ?? '400');
    if (this.ctx.measureText(value).width <= maxWidth) {
      this.text(value, x, y, options);
      return;
    }
    let text = value;
    while (text.length > 1 && this.ctx.measureText(`${text}…`).width > maxWidth) text = text.slice(0, -1);
    this.text(`${text}…`, x, y, options);
  }

  wrap(value: string, maxWidth: number, size = 14): string[] {
    this.font(size);
    const words = value.split(/\s+/);
    const lines: string[] = [];
    let line = '';
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if (this.ctx.measureText(candidate).width > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = candidate;
      }
    }
    if (line) lines.push(line);
    return lines;
  }

  rect(r: Rect, fill: string, stroke?: string, lineWidth = 1): void {
    if (fill) {
      this.ctx.fillStyle = fill;
      this.ctx.fillRect(Math.round(r.x), Math.round(r.y), Math.round(r.w), Math.round(r.h));
    }
    if (stroke) {
      this.ctx.strokeStyle = stroke;
      this.ctx.lineWidth = lineWidth;
      this.ctx.strokeRect(Math.round(r.x) + 0.5, Math.round(r.y) + 0.5, Math.round(r.w) - 1, Math.round(r.h) - 1);
    }
  }

  panel(r: Rect, title?: string): Rect {
    this.rect(r, PALETTE.panel, PALETTE.line);
    if (title) {
      this.rect({ x: r.x, y: r.y, w: r.w, h: Math.round(30 * this.scale) }, PALETTE.panelAlt);
      this.text(title.toUpperCase(), r.x + 12 * this.scale, r.y + 15 * this.scale, {
        size: 13,
        weight: '700',
        colour: PALETTE.accent,
      });
    }
    return r;
  }

  divider(x1: number, y: number, x2: number): void {
    this.ctx.strokeStyle = PALETTE.line;
    this.ctx.lineWidth = 1;
    this.ctx.beginPath();
    this.ctx.moveTo(x1, Math.round(y) + 0.5);
    this.ctx.lineTo(x2, Math.round(y) + 0.5);
    this.ctx.stroke();
  }

  // --------------------------------------------------------------- widgets
  /**
   * A button. `activate` runs on click *and* on Enter/left-click when the
   * button holds the keyboard focus ring.
   */
  button(label: string, r: Rect, activate: () => void, options: ButtonOptions = {}): boolean {
    const disabled = options.disabled === true;
    const pinned = options.pinned === true;
    const hovered = !disabled && this.contains(r, pinned);
    const index = this.register(r, !disabled, activate, options.variant === 'primary' ? -1 : 0, pinned);
    if (hovered) this.hoveredIndex = index;
    const focused = !disabled && !this.mouseUsed && this.navIndex === index && this.items.length > 1;
    const size = options.small ? 12 : 14;

    let fill = PALETTE.panelAlt;
    let ink = PALETTE.ink;
    let stroke = PALETTE.line;
    if (options.variant === 'primary') {
      fill = PALETTE.accent;
      ink = PALETTE.accentInk;
      stroke = '#c99c2b';
    } else if (options.variant === 'danger') {
      fill = '#3a1f1d';
      ink = '#ffb4ac';
      stroke = '#6a2f28';
    } else if (options.variant === 'ghost') {
      fill = 'rgba(0,0,0,0)';
      ink = PALETTE.muted;
    }
    if (hovered && !disabled) {
      fill = options.variant === 'primary' ? '#ffd466' : options.variant === 'danger' ? '#4a2622' : '#262b36';
      ink = options.variant === 'primary' ? PALETTE.accentInk : PALETTE.ink;
    }
    if (disabled) {
      fill = PALETTE.panel;
      ink = '#5b6172';
    }
    this.rect(r, fill, focused ? PALETTE.accent : stroke, focused ? 2 : 1);
    this.font(size, options.variant === 'primary' ? '700' : '600');
    this.ctx.fillStyle = ink;
    this.ctx.textAlign = options.align === 'left' ? 'left' : 'center';
    const tx = options.align === 'left' ? r.x + (options.small ? 8 : 12) * this.scale : r.x + r.w / 2;
    this.ctx.fillText(label, tx, r.y + r.h / 2);
    this.ctx.textAlign = 'left';

    const clicked = hovered && this.pointer.pressed && !disabled;
    if (clicked) activate();
    return clicked;
  }


  toggle(label: string, r: Rect, value: boolean): boolean {
    const hovered = this.contains(r);
    const index = this.register(r, true, undefined);
    if (hovered) this.hoveredIndex = index;
    const focused = !this.mouseUsed && this.navIndex === index && this.items.length > 1;
    this.rect(r, PALETTE.panelAlt, focused ? PALETTE.accent : PALETTE.line, focused ? 2 : 1);
    this.text(label, r.x + 12, r.y + r.h / 2, { size: 13 });
    const boxSize = Math.round(16 * this.scale);
    const box = { x: r.x + r.w - boxSize - 12, y: r.y + r.h / 2 - boxSize / 2, w: boxSize, h: boxSize };
    this.rect(box, value ? PALETTE.accent : '#101218', value ? PALETTE.accent : PALETTE.line);
    if (value) {
      this.text('✓', box.x + boxSize / 2, box.y + boxSize / 2, { size: 12, colour: PALETTE.accentInk, align: 'center', weight: '700' });
    }
    const clicked = hovered && this.pointer.pressed;
    if (clicked) {
      this.items[index]!.activate = undefined;
      return !value;
    }
    return value;
  }

  slider(label: string, r: Rect, value: number, min: number, max: number, step: number, format?: (v: number) => string): number {
    const hovered = this.contains(r);
    const index = this.register(r, true, undefined);
    if (hovered) this.hoveredIndex = index;
    const focused = !this.mouseUsed && this.navIndex === index && this.items.length > 1;
    this.rect(r, PALETTE.panelAlt, focused ? PALETTE.accent : PALETTE.line, focused ? 2 : 1);
    const trackX = r.x + r.w * 0.48;
    const trackW = r.w * 0.4;
    // The label must give way rather than run under the track: in a narrow
    // column "Elimination interval" would otherwise overlap the slider itself.
    this.fitText(label, r.x + 12, r.y + r.h / 2, Math.max(24, trackX - r.x - 24), { size: 13 });
    const trackY = r.y + r.h / 2;
    this.rect({ x: trackX, y: trackY - 2, w: trackW, h: 4 }, '#101218');
    const t = clamp((value - min) / (max - min), 0, 1);
    this.rect({ x: trackX, y: trackY - 2, w: trackW * t, h: 4 }, PALETTE.accent);
    this.rect({ x: trackX + trackW * t - 4, y: trackY - 8, w: 8, h: 16 }, PALETTE.ink);
    this.text(format ? format(value) : value.toFixed(2), r.x + r.w - 12, trackY, {
      size: 12,
      colour: PALETTE.muted,
      align: 'right',
    });

    let next = value;
    if (hovered && (this.pointer.down || this.pointer.pressed)) {
      const local = clamp((this.pointer.x - trackX) / Math.max(1, trackW), 0, 1);
      next = min + local * (max - min);
      if (step > 0) next = Math.round(next / step) * step;
      next = clamp(next, min, max);
    }
    return next;
  }

  /** Returns the newly selected index (or the current one). */
  tabs(labels: string[], r: Rect, active: number): number {
    const gap = 6;
    const tabW = (r.w - gap * (labels.length - 1)) / labels.length;
    let selected = active;
    labels.forEach((label, i) => {
      const tabRect = { x: r.x + i * (tabW + gap), y: r.y, w: tabW, h: r.h };
      const isActive = i === active;
      const hovered = this.contains(tabRect);
      const index = this.register(tabRect, true, i !== active ? () => undefined : undefined, -2);
      if (hovered) this.hoveredIndex = index;
      const focused = !this.mouseUsed && this.navIndex === index && this.items.length > 1;
      this.rect(
        tabRect,
        isActive ? PALETTE.accent : hovered ? '#262b36' : PALETTE.panelAlt,
        focused ? PALETTE.ink : PALETTE.line,
        focused ? 2 : 1,
      );
      this.text(label, tabRect.x + tabRect.w / 2, tabRect.y + tabRect.h / 2, {
        size: 12,
        align: 'center',
        weight: isActive ? '700' : '400',
        colour: isActive ? PALETTE.accentInk : PALETTE.ink,
      });
      if (hovered && this.pointer.pressed && !isActive) selected = i;
    });
    return selected;
  }

  /**
   * Selectable row: draws the chrome, handles hover/focus and fires the click
   * callback. Returns true when the row was clicked this frame.
   */
  row(r: Rect, options: { selected?: boolean; disabled?: boolean; onClick?: () => void; order?: number } = {}): boolean {
    const hovered = !options.disabled && this.contains(r);
    const index = this.register(r, !options.disabled, options.onClick, options.order ?? 0);
    if (hovered) this.hoveredIndex = index;
    const focused = !this.mouseUsed && this.navIndex === index && this.items.length > 1;
    this.rect(
      r,
      options.selected ? '#2c3240' : hovered ? '#232833' : PALETTE.panel,
      focused ? PALETTE.accent : PALETTE.line,
      focused ? 2 : 1,
    );
    const clicked = hovered && this.pointer.pressed && !options.disabled;
    if (clicked && options.onClick) options.onClick();
    return clicked;
  }

  /**
   * A compact selectable chip — a full-width button's small sibling. Registered
   * like every other widget so it is reachable by keyboard and shares the same
   * hover handling.
   */
  chip(label: string, r: Rect, active: boolean, onClick: () => void): void {
    const index = this.register(r, true, onClick);
    const hovered = this.contains(r);
    if (hovered) this.hoveredIndex = index;
    const focused = !this.mouseUsed && this.navIndex === index && this.items.length > 1;
    this.rect(
      r,
      active ? PALETTE.accent : hovered ? '#262b36' : PALETTE.panelAlt,
      focused ? PALETTE.ink : PALETTE.line,
      focused ? 2 : 1,
    );
    this.fitText(label, r.x + r.w / 2, r.y + r.h / 2, r.w - 8 * this.scale, {
      size: 10,
      align: 'center',
      colour: active ? PALETTE.accentInk : PALETTE.ink,
    });
    if (hovered && this.pointer.pressed) onClick();
  }

  /** Horizontal bar used for stats and progress. */
  bar(r: Rect, value01: number, colour = PALETTE.accent): void {
    this.rect(r, '#101218', PALETTE.line);
    this.rect({ x: r.x + 1, y: r.y + 1, w: Math.max(0, (r.w - 2) * clamp(value01, 0, 1)), h: r.h - 2 }, colour);
  }

  /** A small labelled stat bar, as used in the garage. */
  statRow(label: string, value: number, r: Rect): void {
    this.text(label, r.x, r.y + r.h / 2, { size: 12, colour: PALETTE.muted });
    const barRect = { x: r.x + r.w * 0.4, y: r.y + r.h / 2 - 4, w: r.w * 0.6, h: 8 };
    this.bar(barRect, value / 10, value >= 8 ? PALETTE.good : value >= 5 ? PALETTE.accent : PALETTE.warn);
  }
}
